export const TICKLER_CENTER_STATUS_LABELS = {
  OPEN: "Open", IN_PROGRESS: "In Progress", SNOOZED: "Snoozed", COMPLETED: "Completed", CANCELLED: "Cancelled",
  ESCALATED: "Escalated", OVERDUE: "Overdue Status",
} as const;
export type TicklerCenterStatus = keyof typeof TICKLER_CENTER_STATUS_LABELS;
export type TicklerDueState = "OVERDUE" | "DUE_TODAY" | "UPCOMING";

export interface TicklerCenterRecord {
  id: string; companyId: string; clientId: string; customerId: string; accountId: string; placementId: string | null;
  title: string; description: string | null; ticklerType: string; priority: "LOW" | "NORMAL" | "HIGH" | "CRITICAL";
  dueAt: string; snoozedUntil: string | null; effectiveDueAt: string; status: TicklerCenterStatus; statusLabel: string;
  dueState: TicklerDueState | null; dueStateLabel: string | null; actionable: boolean; escalationLevel: number;
  completionOutcome: string | null; assignedUserId: string | null; ownerName: string; assignedTeam: string | null;
  operationalTimezone: string; sourceWorkspace: string; createdBy: string; creatorName: string; createdAt: string;
  updatedBy: string; updaterName: string; updatedAt: string; completedBy: string | null; completerName: string | null;
  completedAt: string | null; version: number; client: { number: string; name: string };
  customer: { number: string; name: string }; account: { number: string; status: string; balanceCents: number };
}
export interface TicklerCenterListResult {
  records: TicklerCenterRecord[]; page: number; pageSize: number; total: number; operationalTimezone: "PER_CLIENT"; operationalDate: null;
  summary: { total: number; actionable: number; overdue: number; dueToday: number; upcoming: number; snoozed: number; escalated: number; completed: number };
  owners: Option[]; assignees: Option[]; customers: Option[]; accounts: Option[];
}
export interface TicklerCenterDetailResult {
  record: TicklerCenterRecord;
  history: Array<{ version: number; changedBy: string; event: string; correlationId: string; createdAt: string; snapshot: Record<string, unknown> }>;
}
export interface TicklerCenterListInput {
  page?: number; pageSize?: number; search?: string; status?: string; priority?: string; ownerId?: string;
  customerId?: string; accountId?: string; dueState?: string; dateFrom?: string; dateTo?: string; sort?: string;
}
interface Option { id: string; name: string }

export async function listTicklerCenter(input: TicklerCenterListInput = {}) {
  const params = new URLSearchParams({ page: String(input.page || 1), pageSize: String(input.pageSize || 25) });
  for (const key of ["search", "status", "priority", "ownerId", "customerId", "accountId", "dueState", "dateFrom", "dateTo", "sort"] as const) {
    if (input[key]) params.set(key, String(input[key]));
  }
  return request<TicklerCenterListResult>(`/api/ar-recovery/ticklers?${params}`);
}
export async function getTicklerCenter(ticklerId: string) {
  return request<TicklerCenterDetailResult>(`/api/ar-recovery/ticklers/${encodeURIComponent(ticklerId)}`);
}
export async function updateTicklerCenter(ticklerId: string, input: { expectedVersion: number; action: string; changes?: Record<string, unknown>; idempotencyKey: string }) {
  return request<{ record: TicklerCenterRecord; replay: boolean }>(`/api/ar-recovery/ticklers/${encodeURIComponent(ticklerId)}`, { method: "PATCH", body: JSON.stringify(input) });
}
async function request<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, { credentials: "same-origin", headers: init?.body ? { "content-type": "application/json" } : undefined, ...init });
  if (!response.ok) { const message = (await response.text()).trim(); throw new Error(message || `Tickler Center request failed (${response.status}).`); }
  return response.json() as Promise<T>;
}
